import type { PostHog } from 'posthog-js';

import { appConfig, isFlagEnabled } from '../config/runtimeConfig';
import { readConsent } from '../utils/consentCookie';

export type Analytics = {
  trackEvent: (name: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
  setUserProps: (props: Record<string, unknown>) => void;
  setConsent: (granted: boolean) => void;
  reset: () => void;
  ready: boolean;
};

export const POSTHOG_API_HOST = 'https://eu.i.posthog.com';

export const createNoopAnalytics = (): Analytics => ({
  trackEvent: () => {},
  identify: () => {},
  setUserProps: () => {},
  setConsent: () => {},
  reset: () => {},
  ready: false,
});

const hasConsent = () => readConsent() === 'accepted';

const loadPostHog = async (): Promise<PostHog | undefined> => {
  if (
    !isFlagEnabled(appConfig.APP_POSTHOG_ENABLED) ||
    !appConfig.APP_POSTHOG_KEY
  ) {
    return undefined;
  }
  try {
    const { default: posthog } = await import('posthog-js');
    if (!posthog.__loaded) {
      const consented = hasConsent();
      posthog.init(appConfig.APP_POSTHOG_KEY, {
        api_host: POSTHOG_API_HOST,
        defaults: '2026-08-30',
        opt_out_capturing_by_default: !consented,
        opt_out_persistence_by_default: !consented,
      });
    }
    return posthog;
  } catch {
    return undefined;
  }
};

type PlausibleFn = (
  event: string,
  options?: { props?: Record<string, unknown> }
) => void;

const trackPlausible = (name: string, props?: Record<string, unknown>) => {
  if (!isFlagEnabled(appConfig.APP_PLAUSIBLE_ENABLED)) return;
  const plausible = (window as Window & { plausible?: PlausibleFn }).plausible;
  plausible?.(name, props ? { props } : undefined);
};

export async function createBrowserAnalytics(): Promise<Analytics> {
  if (typeof window === 'undefined') {
    return createNoopAnalytics();
  }

  const posthog = await loadPostHog();

  return {
    trackEvent: (name, props) => {
      trackPlausible(name, props);
      posthog?.capture(name, props);
    },
    identify: (id, props) => posthog?.identify(id, props),
    setUserProps: (props) => posthog?.setPersonProperties(props),
    setConsent: (granted) => {
      if (!posthog) return;
      if (granted) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
      }
    },
    reset: () => {
      if (!posthog) return;
      posthog.reset();
      if (hasConsent()) {
        posthog.opt_in_capturing({ captureEventName: false });
      }
    },
    ready: true,
  };
}
